import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
} from "firebase/auth";
import { doc, setDoc, collection, query, where, getDocs } from "firebase/firestore";
import { auth, db } from "../firebase";

const PW = "demo1234";

export const DEMO_ACCOUNTS = [
  { role: "lab", email: "lab@demo.com", password: PW, name: "BrightSmile Dental Lab" },
  { role: "designer", email: "designer@demo.com", password: PW, name: "Alex Rivera" },
];

const CASE_THUMB = "https://images.unsplash.com/photo-1468493858157-0da44aaf1d13?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MTN8MHwxfHNlYXJjaHwxfHxkZW50YWwlMjBsYWJ8ZW58MHx8fHwxNzg1OTgyMzk4fDA&ixlib=rb-4.1.0&q=85";

const DESIGNER_PROFILE = {
  experience: "7 years in crown & bridge and implant-supported restorations",
  specialties: "Anterior esthetics, full-arch implant bridges, zirconia crowns",
  software: "exocad DentalCAD, 3Shape Dental System, Meshmixer",
  turnaround: "24-48 hours",
  description:
    "Freelance dental CAD designer focused on natural anterior esthetics and precise implant work. I collaborate closely with labs to deliver print-ready, occlusion-verified designs.",
  languages: "English, Spanish",
  photo:
    "https://images.unsplash.com/photo-1612349317150-e413f6a5b16d?crop=entropy&cs=srgb&fm=jpg&q=85&w=400",
  portfolioItems: [],
  portfolio:
    "300+ delivered restorations for labs across the US and EU. Specialized in high-translucency anterior zirconia and screw-retained implant bridges.",
};

function analysis(en_desc, es_desc, en_sum, es_sum, tags, complexity, warn_en = [], warn_es = []) {
  return {
    description: { en: en_desc, es: es_desc },
    summary: { en: en_sum, es: es_sum },
    tags,
    complexity,
    warnings: { en: warn_en, es: warn_es },
  };
}

function buildCases(labUid, labName, designerUid, designerName) {
  const base = { ownerId: labUid, ownerName: labName, photos: [CASE_THUMB], stlUrl: "", stlName: "", isDemo: true };
  return [
    {
      ...base,
      title: "Anterior zirconia crown #11",
      restorationType: "crown",
      toothNumbers: "11",
      material: "zirconia",
      shade: "A2",
      deadline: "2026-07-05",
      budget: "120",
      clinicalInstructions:
        "Single anterior crown on tooth 11. High-translucency multilayer zirconia. Match adjacent 21, feathered incisal. Light contacts, group function.",
      status: "Published",
      ai: analysis(
        "Single-unit anterior restoration on the maxillary right central incisor (#11) using high-translucency multilayer zirconia. The design must achieve seamless esthetic integration with the adjacent central incisor (#21), including a natural feathered incisal edge. Occlusion requires light contacts with group function guidance.",
        "Restauración unitaria anterior sobre el incisivo central superior derecho (#11) con zirconia multicapa de alta translucidez. El diseño debe lograr una integración estética perfecta con el incisivo central adyacente (#21), incluyendo un borde incisal difuminado natural. La oclusión requiere contactos ligeros con guía de función en grupo.",
        "Anterior zirconia crown #11, shade A2, esthetic match required.",
        "Corona de zirconia anterior #11, color A2, se requiere integración estética.",
        ["zirconia", "crown", "anterior", "esthetic"],
        "Medium",
        [],
        []
      ),
      applicants: [designerUid],
    },
    {
      ...base,
      title: "3-unit posterior bridge #24-26",
      restorationType: "bridge",
      toothNumbers: "24, 25, 26",
      material: "emax",
      shade: "A3",
      deadline: "2026-07-12",
      budget: "260",
      clinicalInstructions:
        "3-unit bridge, 25 pontic. Monolithic lithium disilicate. Check connector dimensions and occlusal clearance.",
      status: "Published",
      ai: analysis(
        "Three-unit fixed bridge spanning the maxillary left quadrant (#24-26) with #25 as the pontic, fabricated in monolithic lithium disilicate (e.max). Careful attention is required to connector cross-sectional dimensions to ensure adequate strength, along with verification of occlusal clearance in the posterior region.",
        "Puente fijo de tres unidades en el cuadrante superior izquierdo (#24-26) con #25 como póntico, fabricado en disilicato de litio monolítico (e.max). Se requiere atención a las dimensiones de los conectores para garantizar resistencia adecuada, junto con la verificación del espacio oclusal en la región posterior.",
        "3-unit e.max posterior bridge #24-26, monolithic.",
        "Puente posterior e.max de 3 unidades #24-26, monolítico.",
        ["bridge", "emax", "posterior", "monolithic"],
        "High",
        ["Confirm minimum connector dimensions for lithium disilicate.", "Antagonist scan not mentioned."],
        ["Confirmar dimensiones mínimas de conector para disilicato de litio.", "No se menciona escaneo del antagonista."]
      ),
      applicants: [],
    },
    {
      ...base,
      title: "Implant abutment + crown #36",
      restorationType: "implant",
      toothNumbers: "36",
      material: "titanium",
      shade: "A3.5",
      deadline: "2026-07-08",
      budget: "180",
      clinicalInstructions:
        "Screw-retained implant crown on 36. Titanium base + zirconia crown. Emergence profile per soft tissue.",
      status: "Published",
      ai: analysis(
        "Screw-retained implant restoration on the mandibular left first molar (#36), combining a titanium base with a zirconia crown superstructure. The emergence profile must be designed according to the soft-tissue contour to support gingival health and function.",
        "Restauración implantosoportada atornillada sobre el primer molar inferior izquierdo (#36), combinando una base de titanio con una supraestructura de corona de zirconia. El perfil de emergencia debe diseñarse según el contorno del tejido blando para favorecer la salud gingival y la función.",
        "Screw-retained implant crown #36 on Ti-base.",
        "Corona implantosoportada atornillada #36 sobre base de titanio.",
        ["implant", "screw-retained", "ti-base", "molar"],
        "High",
        ["Implant system / scan body brand not specified."],
        ["No se especifica el sistema de implante / marca del scan body."]
      ),
      applicants: [designerUid],
    },
    {
      ...base,
      title: "Veneer #21 esthetic",
      restorationType: "veneer",
      toothNumbers: "21",
      material: "emax",
      shade: "BL2",
      deadline: "2026-07-15",
      budget: "95",
      clinicalInstructions:
        "Single feldspathic-look veneer, minimal prep. High value, natural mamelons.",
      status: "Published",
      ai: analysis(
        "Single minimal-preparation veneer on the maxillary left central incisor (#21) in lithium disilicate with a high-value, bleach shade (BL2). The design should replicate natural mamelon structure and a feldspathic-like translucency for a lifelike esthetic result.",
        "Carilla de mínima preparación sobre el incisivo central superior izquierdo (#21) en disilicato de litio con un color de alto valor tipo bleach (BL2). El diseño debe replicar la estructura natural de mamelones y una translucidez tipo feldespática para un resultado estético natural.",
        "Minimal-prep e.max veneer #21, shade BL2.",
        "Carilla e.max de mínima preparación #21, color BL2.",
        ["veneer", "emax", "esthetic", "anterior"],
        "Low",
        [],
        []
      ),
      applicants: [],
    },
    {
      ...base,
      title: "Full-arch implant bridge (upper)",
      restorationType: "bridge",
      toothNumbers: "16-26 (full arch)",
      material: "zirconia",
      shade: "A2",
      deadline: "2026-07-20",
      budget: "850",
      clinicalInstructions:
        "Screw-retained full-arch on 6 implants. Monolithic zirconia with micro-layering on anterior. Verify multi-unit abutment library.",
      status: "In Progress",
      selectedDesignerId: designerUid,
      selectedDesignerName: designerName,
      ai: analysis(
        "Full-arch screw-retained maxillary prosthesis on six implants, fabricated in monolithic zirconia with anterior micro-layering for esthetics. The workflow requires the correct multi-unit abutment library and precise verification of implant positions and prosthetic screw-access channels.",
        "Prótesis maxilar completa atornillada sobre seis implantes, fabricada en zirconia monolítica con micro-estratificación anterior para estética. El flujo de trabajo requiere la biblioteca correcta de pilares multi-unidad y la verificación precisa de las posiciones de los implantes y los canales de acceso de los tornillos.",
        "Full-arch screw-retained zirconia bridge on 6 implants.",
        "Puente completo atornillado de zirconia sobre 6 implantes.",
        ["full-arch", "implant", "zirconia", "screw-retained"],
        "High",
        [],
        []
      ),
      applicants: [designerUid],
    },
    {
      ...base,
      title: "Inlay #46 ceramic",
      restorationType: "inlay",
      toothNumbers: "46",
      material: "emax",
      shade: "A3",
      deadline: "2026-06-28",
      budget: "80",
      clinicalInstructions:
        "MOD inlay, lithium disilicate. Preserve cusps, tight interproximal contacts.",
      status: "Completed",
      selectedDesignerId: designerUid,
      selectedDesignerName: designerName,
      ai: analysis(
        "MOD ceramic inlay on the mandibular right first molar (#46) in lithium disilicate. The restoration must preserve the existing cusps, re-establish tight interproximal contacts, and reproduce functional occlusal anatomy.",
        "Incrustación cerámica MOD sobre el primer molar inferior derecho (#46) en disilicato de litio. La restauración debe preservar las cúspides existentes, restablecer contactos interproximales ajustados y reproducir la anatomía oclusal funcional.",
        "MOD e.max inlay #46, cusp-preserving.",
        "Incrustación MOD e.max #46, preservando cúspides.",
        ["inlay", "emax", "posterior", "mod"],
        "Low",
        [],
        []
      ),
      applicants: [],
    },
  ];
}

async function ensureAccount(email, password) {
  try {
    const cred = await createUserWithEmailAndPassword(auth, email, password);
    return cred.user.uid;
  } catch (e) {
    if (e.code === "auth/email-already-in-use") {
      const cred = await signInWithEmailAndPassword(auth, email, password);
      return cred.user.uid;
    }
    throw e;
  }
}

export async function seedDemoData() {
  const designer = DEMO_ACCOUNTS.find((a) => a.role === "designer");
  const lab = DEMO_ACCOUNTS.find((a) => a.role === "lab");

  const designerUid = await ensureAccount(designer.email, designer.password);
  await setDoc(doc(db, "users", designerUid), {
    uid: designerUid,
    email: designer.email,
    name: designer.name,
    role: "designer",
    profileComplete: true,
    designerProfile: DESIGNER_PROFILE,
    createdAt: Date.now(),
  });

  const labUid = await ensureAccount(lab.email, lab.password);
  await setDoc(doc(db, "users", labUid), {
    uid: labUid,
    email: lab.email,
    name: lab.name,
    role: "lab",
    profileComplete: true,
    createdAt: Date.now(),
  });

  // Idempotency: only seed cases if this lab has none yet (we are signed in as lab here).
  const existing = await getDocs(query(collection(db, "cases"), where("ownerId", "==", labUid)));
  if (existing.empty) {
    const cases = buildCases(labUid, lab.name, designerUid, designer.name);
    const pendingApps = [];
    for (const c of cases) {
      const { applicants, ...caseData } = c;
      const ref = doc(collection(db, "cases"));
      await setDoc(ref, { ...caseData, id: ref.id, createdAt: Date.now(), updatedAt: Date.now() });
      if ((applicants || []).length) pendingApps.push(ref.id);
    }
    // Applications live in a top-level collection; a designer writes their own.
    await signInWithEmailAndPassword(auth, designer.email, designer.password);
    for (const caseId of pendingApps) {
      await setDoc(doc(db, "applications", `${caseId}_${designerUid}`), {
        caseId,
        designerId: designerUid,
        name: designer.name,
        message: "I have extensive experience with this type of restoration and can deliver on time.",
        createdAt: Date.now(),
      });
    }
  }

  await signOut(auth);
}
